// Firebase App (the core Firebase SDK) is always required and
// must be listed before other Firebase SDKs
import firebase from "firebase/compat/app";
import "firebase/compat/auth";
import "firebase/compat/firestore";
import "firebase/compat/storage";

// Firebase services that we use
import "firebase/auth";
import "firebase/firestore";
import "firebase/storage";

// VCE (Vleescentrum) Firebase configuration
const vceFirebaseConfig = {
  apiKey: process.env.REACT_APP_VCE_APIKEY,
  authDomain: process.env.REACT_APP_VCE_AUTHDOMAIN,
  projectId: process.env.REACT_APP_VCE_PROJECTID,
  storageBucket: process.env.REACT_APP_VCE_STORAGEBUCKET,
  messagingSenderId: process.env.REACT_APP_VCE_MESSAGESENDERID,
  appId: process.env.REACT_APP_VCE_APPID,
  measurementId: process.env.REACT_APP_VCE_MEASUREMENTID,
};

// HVO (Halalvlees Online) Firebase configuration
const hvoFirebaseConfig = {
  apiKey: process.env.REACT_APP_HVO_APIKEY,
  authDomain: process.env.REACT_APP_HVO_AUTHDOMAIN,
  projectId: process.env.REACT_APP_HVO_PROJECTID,
  storageBucket: process.env.REACT_APP_HVO_STORAGEBUCKET,
  messagingSenderId: process.env.REACT_APP_HVO_MESSAGESENDERID,
  appId: process.env.REACT_APP_HVO_APPID,
};

// Initialize VCE Firebase (primary app for authentication)
const vceFirebase = firebase.initializeApp(vceFirebaseConfig, "vce");
const vceFirestore = vceFirebase.firestore();

// Initialize HVO Firebase (secondary app)
const hvoFirebase = firebase.initializeApp(hvoFirebaseConfig, "hvo");
const hvoFirestore = hvoFirebase.firestore();

// Use VCE for authentication (default)
export const auth = vceFirebase.auth();

// Export HVO auth as well
export const hvoAuth = hvoFirebase.auth();

/**
 * Login to both VCE and HVO Firebase instances simultaneously
 * @param {string} email - User email
 * @param {string} password - User password
 * @returns {Promise<{vceUser: UserCredential, hvoUser: UserCredential}>}
 */
export const loginToBothFirebase = async (email, password) => {
  console.log('[FirebaseConfig] 🔐 Logging in to both VCE and HVO...');
  
  try {
    // Login to VCE first
    console.log('[FirebaseConfig] 🟢 Logging in to VCE...');
    const vceUserCredential = await auth.signInWithEmailAndPassword(email, password);
    console.log('[FirebaseConfig] ✅ VCE login successful');
    
    // Then login to HVO with same credentials. Any HVO failure means "VCE only" - not only the
    // two old error codes: Firebase 11 answers a wrong or unknown account with
    // auth/invalid-credential, which used to escape as a login error after VCE had succeeded.
    console.log('[FirebaseConfig] 🟣 Logging in to HVO...');
    let hvoUserCredential = null;
    try {
      hvoUserCredential = await hvoAuth.signInWithEmailAndPassword(email, password);
      console.log('[FirebaseConfig] ✅ HVO login successful');
    } catch (hvoError) {
      console.warn('[FirebaseConfig] ⚠️ HVO login failed, continuing with VCE only:', hvoError.code);
    }
    
    return {
      vceUser: vceUserCredential,
      hvoUser: hvoUserCredential,
    };
  } catch (error) {
    console.error('[FirebaseConfig] ❌ Login failed:', error.message);
    // If HVO login fails, that's okay - user might not exist in HVO
    if (error.code === 'auth/user-not-found' || error.code === 'auth/wrong-password') {
      console.warn('[FirebaseConfig] ⚠️ HVO login failed, continuing with VCE only');
      // Check if VCE login succeeded
      if (auth.currentUser) {
        return {
          vceUser: { user: auth.currentUser },
          hvoUser: null,
        };
      }
    }
    throw error;
  }
};

/**
 * Logout from both VCE and HVO Firebase instances
 * @returns {Promise<void>}
 */
export const logoutFromBothFirebase = async () => {
  console.log('[FirebaseConfig] 🚪 Logging out from both VCE and HVO...');
  await Promise.all([
    auth.signOut(),
    hvoAuth.signOut(),
  ]);
  console.log('[FirebaseConfig] ✅ Logged out successfully');
};

// Export both Firebase instances and Firestore instances
export { vceFirebase, hvoFirebase, vceFirestore, hvoFirestore };

// Export VCE as default for compatibility with existing auth code
export default vceFirebase;
